import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { Loader2, CheckCircle2, Truck, AlertCircle, MapPin, Package, Sparkles } from "lucide-react";

/**
 * ShopOrderSuccess — confirmation page polled after Stripe redirect.
 * Calls GET /api/shop/order/:session_id which idempotently fulfils via Gelato.
 */
export default function ShopOrderSuccess() {
  const [sp] = useSearchParams();
  const session_id = sp.get("session_id");
  const { clear } = useCart();

  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!session_id) {
      setError("Session manquante");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/shop/order/${session_id}`);
        if (cancelled) return;
        setOrder(r.data);
        // Once payment is confirmed, clear cart only once.
        if (r.data?.payment_status === "paid" && !clearedRef.current) {
          clearedRef.current = true;
          clear();
        }
        // Stop polling once we have terminal state
        const done = r.data?.payment_status === "paid" && (r.data?.gelato_order_id || r.data?.gelato_error);
        const terminalCancelled = ["expired", "cancelled"].includes(r.data?.payment_status);
        if (!done && !terminalCancelled && attempts < 12) {
          setTimeout(() => setAttempts((a) => a + 1), 2500);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Commande introuvable");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [session_id, attempts, clear]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 text-center" data-testid="shop-order-error">
        <AlertCircle className="w-12 h-12 mx-auto text-[#E5276A] mb-3" />
        <h1 className="font-display text-2xl text-[#1F3A5F]">Une erreur est survenue</h1>
        <p className="text-[#4A5568] mt-2">{error}</p>
        <Link to="/shop" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#1F3A5F] text-white text-sm font-bold">
          Retour à la boutique
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-16 flex items-center gap-2 text-[#8B95A1]" data-testid="shop-order-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Vérification du paiement…
      </div>
    );
  }

  const paid = order.payment_status === "paid";
  const fulfilled = !!order.gelato_order_id;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-12" data-testid="shop-order-success-page">
      <div className="text-center">
        {paid ? (
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#22C55E] mb-3" />
        ) : (
          <Loader2 className="w-16 h-16 mx-auto text-[#E5276A] animate-spin mb-3" />
        )}
        <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F]" data-testid="shop-order-status">
          {paid ? "Merci pour votre commande !" : "Vérification du paiement…"}
        </h1>
        <p className="text-[#4A5568] mt-3 max-w-xl mx-auto">
          {paid
            ? `Votre commande #${(order.order_id || "").slice(0, 8)} est confirmée. Un email de confirmation vient d'être envoyé à ${order.shipping?.email || "votre adresse"}.`
            : "Stripe vérifie votre paiement, cela ne prend que quelques secondes…"}
        </p>
      </div>

      {paid && (
        <div className="mt-10 space-y-4">
          {/* Production status */}
          <div className="p-5 bg-white border border-[#1F3A5F]/12 rounded-2xl flex items-start gap-4">
            <Package className="w-6 h-6 text-[#E5276A] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-lg text-[#1F3A5F]">État de la production</h2>
              {fulfilled ? (
                <>
                  <p className="text-sm text-[#4A5568] mt-1">
                    <Sparkles className="inline w-3.5 h-3.5 text-[#22C55E]" /> Commande transmise à notre atelier d&apos;impression.
                  </p>
                  <p className="text-xs text-[#8B95A1] mt-2">
                    Référence production : <code className="bg-[#FBF7F3] px-2 py-0.5 rounded">{(order.gelato_order_id || "").slice(0, 12)}…</code>
                  </p>
                </>
              ) : order.gelato_error ? (
                <p className="text-sm text-[#E5276A] mt-1" data-testid="shop-order-fulfill-error">
                  ⚠️ La commande n&apos;a pas pu être transmise automatiquement. Notre équipe a été notifiée et la traitera manuellement sous 24 h.
                </p>
              ) : (
                <p className="text-sm text-[#4A5568] mt-1">
                  <Loader2 className="inline w-3.5 h-3.5 animate-spin" /> Transmission à notre partenaire d&apos;impression en cours…
                </p>
              )}
            </div>
          </div>

          {/* Shipping */}
          <div className="p-5 bg-white border border-[#1F3A5F]/12 rounded-2xl">
            <h2 className="font-display text-lg text-[#1F3A5F] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#E5276A]" /> Adresse de livraison
            </h2>
            {order.shipping && (
              <div className="mt-3 text-sm text-[#4A5568] leading-relaxed">
                <b className="text-[#1F3A5F]">{order.shipping.first_name} {order.shipping.last_name}</b><br />
                {order.shipping.address_line1}{order.shipping.address_line2 ? <><br />{order.shipping.address_line2}</> : null}<br />
                {order.shipping.post_code} {order.shipping.city}{order.shipping.state ? `, ${order.shipping.state}` : ""}<br />
                {order.shipping.country}
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 text-xs text-[#4A5568]">
              <Truck className="w-3.5 h-3.5 text-[#E5276A]" />
              Livraison estimée 5-7 jours ouvrés en UE · 7-14 jours hors UE
            </div>
          </div>

          {/* Items */}
          <div className="p-5 bg-white border border-[#1F3A5F]/12 rounded-2xl">
            <h2 className="font-display text-lg text-[#1F3A5F] mb-3">Détail</h2>
            <div className="space-y-2.5">
              {(order.items || []).map((i) => (
                <div key={i.variant_id} className="flex items-center justify-between text-sm">
                  <div className="flex-1 min-w-0 pr-3">
                    <div className="font-bold text-[#1F3A5F] truncate">{i.product_title || "Carte geoVINUM"}</div>
                    <div className="text-xs text-[#8B95A1]">{i.variant_title} · ×{i.quantity}</div>
                  </div>
                  <div className="font-bold text-[#1F3A5F] whitespace-nowrap">{(i.unit_price_eur * i.quantity).toFixed(2)} €</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#1F3A5F]/10 text-sm">
              <div className="flex justify-between text-[#4A5568]"><span>Sous-total</span><span className="font-bold text-[#1F3A5F]">{Number(order.subtotal_eur).toFixed(2)} €</span></div>
              <div className="flex justify-between text-[#4A5568]"><span>Livraison</span><span className="font-bold text-[#1F3A5F]">{Number(order.shipping_eur).toFixed(2)} €</span></div>
              <div className="flex justify-between mt-2 pt-2 border-t border-[#1F3A5F]/10 items-baseline">
                <span className="font-bold text-[#1F3A5F]">Total payé</span>
                <span className="font-display text-xl text-[#E5276A]" data-testid="shop-order-total">{Number(order.total_eur).toFixed(2)} €</span>
              </div>
            </div>
          </div>

          <div className="text-center pt-4">
            <Link to="/shop" data-testid="shop-order-continue"
              className="inline-block px-6 py-3 rounded-full bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870] transition-colors">
              Continuer mes achats
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
